import {Fragment, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {CalendarDayOverviewCardProps} from './types';

import {Icon} from '@atoms';
import {useFeatureToggle, useModal} from '@hooks';
import {
  getAllConnectedUsers,
  getHasUserGivenCalendarConsent,
  getIsMeetingsCalendarEnabled,
  getIsUserCalendarConsentInvalid,
  getParkingReservationByDate,
  useAppSelector,
  useRelevantConnections,
} from '@lib/store';
import {getWorkdaysWithWorkspaceReservationsForDate} from '@lib/store/src/data/timeslots/selectors';
import {isBeforeToday} from '@lib/utils';
import {AvatarStack, Button, UserCell} from '@molecules';
import {CalendarEventsList, ConnectionOverview, CreateEventCard, ModalPage} from '@organisms';
import {parseISO, startOfToday} from 'date-fns';
import {NoOneThere} from '../../home/Connections/EmptyState';
import {TileItem} from './components/TileItem';
import {AddExtraServicesTile, ExtrasTile} from './components/tiles/ExtrasTile';
import {WorkdayTile_timeslots} from './components/tiles/WorkdayTile/WorkdayTile_timeslots';
import {WorkspaceTile_timeslots} from './components/tiles/WorkspaceTile/WorkspaceTile_timeslots';
import {StyledCard} from './styles';
import {useConnectionsSchedule} from '@lib/store/src/ui/hooks/timeslots/useConnectionsSchedule';
import {LocalDateString} from '@lib/store/src/data/timeslots/apiTypes';
import {Div, P} from '@quarks';
import {ConnectionOverview_timeslots} from '../../home/Connections/ConnectionsOverview_timeslots';
import {GroupBookingTile_timeslots} from './components/tiles/GroupBookingTile/GroupBookingTile_timeslots';
import {constructConnectionsOverview} from '@lib/store/src/data/timeslots/viewmodel-helpers/constructConnectionsOverview';
import {ParkingTile} from './components/tiles/ParkingTile';
import {isOfficeWorkday} from '@lib/store/src/data/timeslots/utils';

export const CalendarDayOverview_timeslots = ({className, date: dateKey}: CalendarDayOverviewCardProps) => {
  const {t} = useTranslation();
  const {HybridMeetingsCalendarView, ExtrasFeature, ContextAwareExtras, Parking} = useFeatureToggle();
  const isMeetingCalendarEnabled = useAppSelector(getIsMeetingsCalendarEnabled);

  const {addPages, openModal} = useModal();

  const parsedDate = useMemo(() => parseISO(dateKey), [dateKey]);

  const {connectionInOfficeByBuilding} = useRelevantConnections(dateKey);

  const allConnectionsAccrossAllBuildings = useMemo(() => {
    return connectionInOfficeByBuilding
      ? Object.values(connectionInOfficeByBuilding).flatMap((x) => x.data.map((y) => y.user))
      : [];
  }, [connectionInOfficeByBuilding]);

  const reservations = useAppSelector(getWorkdaysWithWorkspaceReservationsForDate(dateKey));

  const isPast = isBeforeToday(parsedDate, startOfToday());
  const isCalendarEnabledByAdmin = useAppSelector(getIsMeetingsCalendarEnabled);
  const isCalendarConsentGiven = useAppSelector(getHasUserGivenCalendarConsent);
  const isCalendarConsentInvalid = useAppSelector(getIsUserCalendarConsentInvalid);

  const shouldShowMeetingCreationButton = isPast // Remove the button for the days in the past
    ? false
    : HybridMeetingsCalendarView && isCalendarEnabledByAdmin && isCalendarConsentGiven && !isCalendarConsentInvalid;

  const showMeetingsSection = HybridMeetingsCalendarView && isMeetingCalendarEnabled;

  // Parking
  const parkingReservation = useAppSelector((state) => getParkingReservationByDate(state, dateKey));

  const firstOfficeWorkday = reservations?.workdays.map((r) => r.workday).find(isOfficeWorkday);

  const parkingTile = Parking && firstOfficeWorkday && (
    <ParkingTile
      date={dateKey}
      buildingId={firstOfficeWorkday.buildingId}
      active={!!parkingReservation}
      isLastActive={true}
    />
  );

  // Invites
  const invitationTiles =
    reservations?.unparented.flatMap((r, i, rs) => {
      if (r.invitationTo && r.status === 'InvitationPending')
        return (
          <GroupBookingTile_timeslots
            key={r.id}
            date={dateKey}
            active={true}
            isLastActive={i === rs.length - 1 && (!parkingTile || !parkingReservation)}
            workspaceReservation={r}
            workday={null}
          />
        );

      console.warn('Warning: unparrented reservation found', r);
      return [];
    }) ?? [];

  return (
    <StyledCard className={className}>
      <div className="grid grid-cols-[auto_1fr_auto]">
        {/* Unknown location tile */}
        {reservations?.workdays.length === 0 && (
          <WorkdayTile_timeslots
            workday={null}
            date={dateKey}
            active={true}
            isLastActive={reservations?.unparented.length === 0}
          />
        )}

        {reservations?.workdays.map((data, wdIdx, wds) => {
          const isLastWorkday = wdIdx === wds.length - 1;
          return (
            <Fragment key={wdIdx}>
              <WorkdayTile_timeslots
                workday={data.workday}
                date={dateKey}
                active={true}
                isLastActive={isLastWorkday && data.workspaceReservations.length === 0}
              />

              {data.workspaceReservations.map((r, rIdx, rs) => {
                if (data.workday.status !== 'OfficeDay') {
                  throw new Error('Encountered non-office workday with workspace reservations');
                }
                const isLastReservation = rIdx === rs.length - 1;

                const Tile = r.status === 'InvitationPending' ? GroupBookingTile_timeslots : WorkspaceTile_timeslots;

                const workday = data.workday?.status === 'OfficeDay' ? data.workday : null;

                return (
                  <Tile
                    key={rIdx}
                    date={dateKey}
                    active={true}
                    isLastActive={isLastReservation && isLastWorkday && invitationTiles.length === 0 && !parkingTile}
                    workspaceReservation={r}
                    workday={workday}
                  />
                );
              })}

              {/* TODO: once we allow multiple bookings, this shouldn't check existing reservation length */}
              {data.workday.status === 'OfficeDay' && data.workspaceReservations.length === 0 && (
                <WorkspaceTile_timeslots
                  active={false}
                  date={dateKey}
                  isLastActive={!parkingTile && invitationTiles.length === 0}
                  workspaceReservation={null}
                  workday={data.workday}
                />
              )}
            </Fragment>
          );
        })}

        {invitationTiles}

        {ExtrasFeature ? (
          ContextAwareExtras ? (
            <AddExtraServicesTile date={dateKey} />
          ) : (
            <ExtrasTile date={dateKey} />
          )
        ) : null}

        {parkingTile}

        {/* {tiles.map(({tile, active}, index) => {
          switch (tile) {

            case 'WorkspaceTile':
              return (
                <WorkspaceTile
                  key={tile + index}
                  date={dateKey}
                  active={active}
                  isLastActive={active && index === lastActiveIndex}
                />
              );
            case 'ParkingTile':
              return (
                workday && (
                  <ParkingTile
                    key={tile + index}
                    date={dateKey}
                    buildingId={workday.nodeId}
                    active={active}
                    isLastActive={active && index === lastActiveIndex}
                  />
                )
              );

            case 'GroupBookingTile':
              // useTiles only returns one instance of GroupBookingTile
              // lastActiveIndex also take the lenght of the pendingWorkspaceReservations into account
              return (pendingWorkspaceReservations || []).map((wr, groupIndex) => (
                <GroupBookingTile
                  workspaceReservation={wr}
                  showMapPreview={pendingWorkspaceReservations?.length === 1 && !workspaceReservation}
                  key={tile + index}
                  date={dateKey}
                  active={active}
                  isLastActive={active && index + groupIndex === lastActiveIndex}
                />
              ));

            default:
              return null;
          }
        })} */}
      </div>

      <div className="grid grid-cols-[auto_1fr_auto]">
        <ConnectionsSection
          mode={showMeetingsSection ? 'summary' : 'full-content'}
          date={dateKey}
        />
      </div>

      {showMeetingsSection && (
        <div className="grid grid-cols-[auto_1fr_auto]">
          <span className="flex justify-between items-center col-span-full">
            <h3 className="col-span-full">{t('meeting:YourSchedule')}</h3>
            {shouldShowMeetingCreationButton && (
              <Button
                onClick={() => {
                  addPages([<CreateEventCard date={dateKey} />]);
                  openModal();
                }}
                button="tertiary">
                <Icon
                  icon="plus"
                  className="size-4"
                />
                {t('meeting:AddEvent')}
              </Button>
            )}
          </span>

          <div className="col-span-full mt-4">
            <CalendarEventsList
              date={dateKey}
              hideCreationButton
            />
          </div>
        </div>
      )}
    </StyledCard>
  );
};

const ConnectionsSection = ({mode, date: dateKey}: {mode: 'summary' | 'full-content'; date: LocalDateString}) => {
  const {t} = useTranslation();
  const {addPages, openModal, closeModal} = useModal();
  const schedule = useConnectionsSchedule(dateKey);
  const connectedUsers = useAppSelector(getAllConnectedUsers);

  const overview = constructConnectionsOverview(connectedUsers, schedule.workspaceResevations);

  const relevantConnectedUsers = overview.flatMap((o) => o.occupants.map((occ) => occ.user));
  if (relevantConnectedUsers.length === 0) {
    return (
      <span className="col-span-full mt-4">
        <NoOneThere />
      </span>
    );
  }

  if (mode === 'summary') {
    return (
      <>
        <h3 className="col-span-full">{t('connection:AtTheOffice')}</h3>

        <TileItem
          className="bg-beige-200 [&>*:first-child]:self-center [&>*:last-child]:self-center"
          onClick={() => {
            addPages([
              <ModalPage
                title="Connections at the office"
                onClose={closeModal}>
                <div className="py-4">
                  <ConnectionOverview_timeslots
                    dateKey={dateKey}
                    hideTitle
                  />
                </div>
              </ModalPage>,
            ]);
            openModal();
          }}>
          <span className="col-span-2">
            <AvatarStack
              date={dateKey}
              overlap
              users={relevantConnectedUsers}
            />
          </span>
          <Icon
            icon="caretRight"
            color="currentColor"
            className="text-mapiq-black-800* size-5"
          />
        </TileItem>
      </>
    );
  }

  return (
    <div className="col-span-full mt-4">
      {overview.map((o, i) => {
        return (
          <Div key={i}>
            <P
              fontSize="sm"
              fontWeight="bold">
              {o.isUserWorkspace ? t('connection:YourLocation') : o.locationName}
            </P>
            {o.occupants.map((occ) => (
              <UserCell
                separator={false}
                padding="small"
                key={occ.user.id}
                user={occ.user}
              />
            ))}
          </Div>
        );
      })}
    </div>
  );
};
